import React from 'react'
import moment from 'moment'
import { BreadcrumbView } from '../../../../../context/breadcrumb.context'
import { useNotificationContext } from '../../../../../context/notification/context'
import { IconCalender, IconList } from '../../../../../shared/icon/icon'
import { Menu, MenuItem, MenuPurge, MenuRestore } from '../../../../../shared/menu/menu'
import { CardFullHeightScrollY } from '../../../../../shared/card/card-full-height-scroll-y'
import { useApiListOrganizationRecycle } from '../../../../../api-new/state/content'
import { useRoleSysOrganization } from '../../../../../role/hook/role-sys'
import { TableAgClient } from '../../../../../shared/table-builder/table-ag-client'
import { ColumnText, ColumnMenu, ColumnFormat } from '../../../../../shared/table-builder/table-columns'
import { serverMessage } from '../../../../../shared/error/server-message'
import { ErrorReference } from '../../../../../shared/error/error-reference'
import { useErrorLine } from '../../../../../shared/error/use-error-line'

// The sys recycle bin over the organization deletion lifecycle (delete-organization
// plan Task 24). Each row is an organization switched off by a scheduled deletion,
// or a legacy one soft-deleted before scheduling existed (state "legacy": never
// purged automatically, restore only).

const STATE_TEXT = {
  scheduled: 'Scheduled',
  held: 'Held',
  purging: 'Purging',
  failed: 'Failed',
  legacy: 'Deleted before scheduling'
}

const SOURCE_TEXT = { owner: 'Owner', sys_admin: 'Support' }

// Content commit 91c8c017: purge_counts.shared_files, carried by the sys
// deleted-grid API as sharedFiles -- stored files the purge kept because
// another organization still references them. 0/missing renders as a dash;
// anything kept gets a short native tooltip so the row explains itself
// without a dedicated column of prose.
const SHARED_FILES_HINT = 'Files still used by another organization were not deleted. Support was emailed.'
const sharedFilesText = value => (value ? <span title={SHARED_FILES_HINT}>{value}</span> : '—')

// The purge date as a day ("1 Nov 2026"), in UTC like the emails.
export const purgeDay = value => (value ? moment.utc(value).format('D MMM YYYY') : '')

const restorable = row => row.state !== 'purging' && row.state !== 'purged'
// Only a scheduled or failed deletion can be purged now (review I1): a held one,
// with its hold standing or lifted, is rescheduled with a fresh 7-day window
// instead, and the server refuses it (409 held).
const purgeable = row => !row.legalHold && (row.state === 'scheduled' || row.state === 'failed')
const extendable = row => row.state === 'scheduled' || row.state === 'held'

export function OrganizationRecycle() {
  const apiListOrganization = useApiListOrganizationRecycle()

  const roleSysOrganization = useRoleSysOrganization()

  const notificationContext = useNotificationContext()

  const [error, failure, setError] = useErrorLine()

  const handleReady = async () => await apiListOrganization.get('deleted')
  const handleDestroyed = () => apiListOrganization.init()

  const handleRestore = async row => {
    setError(null)
    try {
      await apiListOrganization.put(`${row.id}/restore`)
      notificationContext.toast.notify('Organization restored', 3000)
    } catch (err) {
      setError(serverMessage(err, "This organization couldn't be restored."), err)
    }
    await apiListOrganization.get('deleted')
  }

  return (
    <React.Fragment>
      <BreadcrumbView Icon={IconList} text="Recycle bin">
        <CardFullHeightScrollY title="recycle bin">
          {error &&
            <p className="text-danger" role="alert">
              {error}
            </p>}
          {error && <ErrorReference error={failure} className="mb-3" />}
          <TableAgClient apiList={apiListOrganization} hideFloatingFilter onReady={handleReady} onDestoryed={handleDestroyed}>
            <ColumnText field="name" header="Name" />
            <ColumnFormat field="state" header="State" render={cell => STATE_TEXT[cell.value] || cell.value || ''} />
            <ColumnFormat field="purgeAfter" header="Purge on" render={cell => purgeDay(cell.value)} />
            <ColumnText field="requestedBy" header="Requested by" />
            <ColumnFormat field="source" header="Source" render={cell => SOURCE_TEXT[cell.value] || ''} />
            <ColumnFormat field="legalHold" header="Legal hold" render={cell => (cell.value ? 'Yes' : '')} />
            <ColumnText field="lastError" header="Last error" />
            <ColumnFormat field="sharedFiles" header="Files kept (shared)" render={cell => sharedFilesText(cell.value)} />
            <ColumnMenu
              menu={row =>
                <Menu small>
                  <MenuRestore onClick={() => handleRestore(row)} show={roleSysOrganization.accessDelete && restorable(row)} />
                  <MenuPurge
                    text="purge now"
                    to={`/admin/organization/list/recycle/purge/${row.id}`}
                    show={roleSysOrganization.accessPurge && purgeable(row)}
                  />
                  <MenuItem
                    Icon={IconCalender}
                    text="extend"
                    to={`/admin/organization/list/recycle/extend/${row.id}`}
                    show={roleSysOrganization.accessDelete && extendable(row)}
                  />
                </Menu>}
            />
          </TableAgClient>
        </CardFullHeightScrollY>
      </BreadcrumbView>
    </React.Fragment>
  )
}
